import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setDone(true);
    setEmail("");
    setTimeout(() => setDone(false), 4000);
  };

  return (
    <footer
      id="waitlist"
      className="relative bg-[#0A0B0D] border-t border-[#1F2126] overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-[1700px] mx-auto px-6 sm:px-10 lg:px-12 pt-24 sm:pt-32 pb-10">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3 t-mono text-[#7A7A78]">
            — § 09 / Contact
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 1.2, ease }}
            className="col-span-12 md:col-span-9"
          >
            <h2 className="t-display text-[#F4F2EC] text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.04em] max-w-3xl">
              Speak with a relationship manager.
            </h2>

            <p className="mt-6 t-body text-sm text-[#BFBDB6] leading-relaxed max-w-2xl">
              Whether you're an existing private client, a family-office
              principal, or exploring access to investment mandates — a
              relationship manager will respond within one business hour.
            </p>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start max-w-4xl">
              <div className="lg:col-span-5 grid grid-cols-1 gap-8">
                <div>
                  <div className="t-mono text-[#7A7A78] mb-2">Contact us</div>
                  <a
                    href="mailto:support@creditstone.io"
                    className="t-body text-base text-[#F4F2EC] link-underline"
                    data-testid="footer-email-link"
                  >
                    support@creditstone.io
                  </a>
                  <p className="mt-2 t-body text-xs text-[#7A7A78]">
                    For most things, this is the fastest path.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="t-mono text-[#7A7A78] mb-3">Request access in 60 seconds</div>
                <form
                  onSubmit={onSubmit}
                  className="flex items-center gap-2 border-b border-[#2A2C30] pb-3"
                  data-testid="waitlist-form"
                >
                  <input
                    type="email"
                    required
                    placeholder="you@institution.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-transparent outline-none placeholder:text-[#5a5a5a] text-[#F4F2EC] t-body text-sm tracking-wide"
                    data-testid="waitlist-email-input"
                  />
                  <button
                    type="submit"
                    className="group flex items-center gap-2 t-mono text-[#F4F2EC]"
                    data-testid="waitlist-submit-btn"
                  >
                    {done ? "Received" : "Request access"}
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                </form>
                {done && (
                  <p
                    className="mt-3 t-mono text-[#F4F2EC]/70"
                    data-testid="waitlist-success"
                  >
                    Thank you. A relationship manager will be in touch.
                  </p>
                )}
                <p className="mt-5 t-body text-xs text-[#7A7A78] leading-relaxed max-w-md">
                  Existing private client · Request access / onboarding ·
                  Investment enquiry · Family office / institutional · Press &amp;
                  partnerships
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-28 mb-12 grid grid-cols-12 gap-6 pt-12 border-t border-[#1F2126]">
          <div className="col-span-12 md:col-span-5">
            <img
              src="/csd-logo.png"
              alt="Credit Stone Digital"
              className="h-12 w-auto select-none"
              draggable="false"
              style={{ filter: "brightness(1.18) contrast(1.05)" }}
            />
            <p className="mt-5 t-body text-sm text-[#BFBDB6] leading-relaxed max-w-xs">
              A private financial ecosystem for global investors. Track hedge
              fund portfolios, access investment fund opportunities, and
              connect to digital banking through one secure platform.
            </p>
          </div>

          <div className="col-span-6 md:col-span-3 md:col-start-7">
            <div className="t-mono text-[#5a5a5a] mb-4">Clients</div>
            <ul className="space-y-2.5">
              {["Private", "Family offices", "Institutional", "Contact"].map((l) => (
                <li key={l}>
                  <a href="#audiences" className="link-underline t-body text-sm text-[#F4F2EC]/85 hover:text-[#F4F2EC]">
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2 md:col-start-11">
            <div className="t-mono text-[#5a5a5a] mb-4">Legal</div>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="/disclosures"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline t-body text-sm text-[#F4F2EC]/85 hover:text-[#F4F2EC]"
                  data-testid="footer-disclosures-link"
                >
                  Disclosures
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 t-mono text-[#5a5a5a]">
          <div>© Credit Stone Digital</div>
          <div>Private clients · 20+ countries</div>
        </div>
      </div>
    </footer>
  );
}
